import { Box } from '@wix/design-system';
import React, { useContext } from 'react';

import { GALLERY_TYPE } from './../../../../ricos-content/consts';
import { RicosContext } from './../../../../ricos-context';
import type { LogBiFn } from './../../../../_shared/bi-context-provider/bi-context-provider';
import { useNodeAttrs } from './../../../../_shared/use-node-attrs';
import { useSpoilerPlugin } from './../../../../_shared/use-spoiler-plugin';
import { ToggleButton, usePanelContainerElement } from './../../../../ui-components';

import {
  GENERAL_SETTINGS_DOWNLOAD_TOGGLE,
  GENERAL_SETTINGS_EXPAND_TOGGLE,
  GENERAL_SETTINGS_SPOILER_TOGGLE,
} from '../../../data-hooks';

export const GeneralSettings: React.FC<{
  nodeId: string;
  logBi: LogBiFn;
}> = ({ nodeId, logBi }) => {
  const { t } = useContext(RicosContext);
  const isSpoilerEnabled = useSpoilerPlugin(GALLERY_TYPE);
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'GALLERY');
  const containerEl = usePanelContainerElement();

  return (
    <Box direction="vertical" height="100%" gap="SP3">
      <ToggleButton
        title={t('GalleryPlugin_Settings_ImagesOpenInExpandMode_Label')}
        checked={!pmNodeAttrs?.disableExpand}
        onChange={() => {
          logBi({ dataHook: GENERAL_SETTINGS_EXPAND_TOGGLE, value: !!pmNodeAttrs?.disableExpand });
          patchPmNodeAttrs?.({ disableExpand: !pmNodeAttrs?.disableExpand });
        }}
        infoContent={t('GallerySettings_Expand_Mode_Toggle')}
        dataHook={GENERAL_SETTINGS_EXPAND_TOGGLE}
        size="small"
        tooltipAppendTo={containerEl}
      />
      <ToggleButton
        title={t('GalleryPlugin_Settings_ImagesCanBeDownloaded_Label')}
        checked={!pmNodeAttrs?.disableDownload}
        onChange={() => {
          logBi({
            dataHook: GENERAL_SETTINGS_DOWNLOAD_TOGGLE,
            value: !!pmNodeAttrs?.disableDownload,
          });
          patchPmNodeAttrs?.({ disableDownload: !pmNodeAttrs?.disableDownload });
        }}
        infoContent={t('GalleryPlugin_Settings_ImagesCanBeDownloaded_Tooltip')}
        dataHook={GENERAL_SETTINGS_DOWNLOAD_TOGGLE}
        size="small"
        tooltipAppendTo={containerEl}
      />
      {isSpoilerEnabled && (
        <ToggleButton
          title={t('GallerySettings_Spoiler_Toggle')}
          checked={Boolean(pmNodeAttrs?.containerData?.spoiler?.enabled)}
          onChange={() => {
            logBi({
              dataHook: GENERAL_SETTINGS_SPOILER_TOGGLE,
              value: !pmNodeAttrs?.containerData?.spoiler?.enabled,
            });
            patchPmNodeAttrs?.({
              containerData: {
                ...pmNodeAttrs?.containerData,
                spoiler: {
                  ...pmNodeAttrs?.containerData?.spoiler,
                  enabled: !pmNodeAttrs?.containerData?.spoiler?.enabled,
                },
              },
            });
          }}
          infoContent={t('Spoiler_Toggle_Tooltip')}
          dataHook={GENERAL_SETTINGS_SPOILER_TOGGLE}
          size="small"
          tooltipAppendTo={containerEl}
        />
      )}
    </Box>
  );
};
